/**
 * Premenovať časť / výpočet (KOLO 203). PATCH `nazov` server vedel
 * odjakživa, appka ho nepoužívala — časť z Tandem Zostáv má meno z Určení
 * (`Skrinka prava`) a user ho chce mať po svojom. Párovanie ďalšieho
 * exportu ide podľa uloženého dovetku (`urceni_casti`), premenovanie ho
 * nepokazí.
 */
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import { useUpdateVarianta } from "@/api/hooks/useVarianty";
import type { Varianta } from "@/api/schemas";

export function PremenovatVariantuDialog({
  zakazkaId,
  varianta,
  onOpenChange,
}: {
  zakazkaId: string;
  /** `null` = zatvorené. */
  varianta: Varianta | null;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const update = useUpdateVarianta(zakazkaId);
  const [nazov, setNazov] = useState("");
  useEffect(() => {
    if (varianta) setNazov(varianta.nazov);
  }, [varianta]);

  const co = varianta?.je_cast ? "časť" : varianta?.je_vyrobna ? "výrobnú variantu" : "výpočet";
  const novy = nazov.trim();

  async function uloz(): Promise<void> {
    if (!varianta || !novy || novy === varianta.nazov) return;
    try {
      await update.mutateAsync({ id: varianta.id, data: { nazov: novy } });
      toast({ title: "Premenované", description: `${varianta.nazov} → ${novy}` });
      onOpenChange(false);
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Premenovanie neprešlo",
        description: extractErrorMessage(e),
      });
    }
  }

  return (
    <Dialog open={varianta !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Premenovať {co}</DialogTitle>
          <DialogDescription>
            {varianta?.urceni_casti
              ? `Ďalší export z TopSolidu sa s ňou spáruje ďalej podľa Určení (${varianta.urceni_casti}).`
              : "Názov záložky v zákazke."}
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-1.5"
          onSubmit={(e) => {
            e.preventDefault();
            void uloz();
          }}
        >
          <Label htmlFor="premenovat-nazov">Názov</Label>
          <Input
            id="premenovat-nazov"
            value={nazov}
            maxLength={50}
            autoFocus
            onChange={(e) => setNazov(e.target.value)}
          />
        </form>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Zrušiť
          </Button>
          <Button
            disabled={!novy || novy === varianta?.nazov || update.isPending}
            onClick={() => void uloz()}
          >
            Uložiť
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
